import React, { useEffect, useState } from 'react';
import { fetchReplay, saveReplayFile, verifyReplaySignature } from '../lib/replay';

/**
 * Signed launch-replay viewer modal.
 *
 * Renders every trade on a clan in a readable table (µs arrival,
 * side, qty, avg price, batch position, opaque user hash) and
 * verifies the ed25519 signature in-browser using Web Crypto so
 * the "Signature verified" badge is *live*, not a server claim.
 *
 * Props:
 *   open    boolean — controls visibility
 *   handle  string  — clan handle to replay
 *   onClose fn      — parent close handler
 */
export default function ReplayModal({ open, handle, onClose }) {
  const [state, setState] = useState({ loading: true, error: null, bundle: null, pub: null, verify: null });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !handle) return undefined;
    let cancelled = false;
    (async () => {
      setState({ loading: true, error: null, bundle: null, pub: null, verify: null });
      try {
        const { bundle, pub } = await fetchReplay(handle);
        if (cancelled) return;
        // Fire verification in parallel with the render — the badge
        // flips from "Verifying…" → ✓/✗ once the promise resolves.
        setState({ loading: false, error: null, bundle, pub, verify: 'pending' });
        const v = await verifyReplaySignature(bundle, pub.public_key_b64);
        if (!cancelled) setState((s) => ({ ...s, verify: v }));
      } catch (e) {
        if (!cancelled) {
          setState({
            loading: false,
            error: e?.response?.data?.detail || 'Could not load the signed replay.',
            bundle: null, pub: null, verify: null,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, handle]);

  if (!open) return null;

  const { loading, error, bundle, pub, verify } = state;
  const payload = bundle?.payload;
  const trades = payload?.trades || [];

  const onDownload = () => {
    if (bundle && pub) saveReplayFile(bundle, pub, handle);
  };
  const onCopySig = async () => {
    if (!bundle?.signature) return;
    try {
      await navigator.clipboard.writeText(bundle.signature);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className="replay-modal-backdrop" onClick={onClose} data-testid="replay-modal">
      <div className="replay-modal" onClick={(e) => e.stopPropagation()}>
        <div className="replay-modal-head">
          <div>
            <div className="replay-modal-title">Signed replay · @{handle}</div>
            <div className="replay-modal-sub">
              Every trade, in the exact order the engine ran them — cryptographically signed by Clanster.
            </div>
          </div>
          <button
            type="button"
            className="replay-modal-close"
            onClick={onClose}
            aria-label="Close signed replay"
            data-testid="replay-modal-close"
          >×</button>
        </div>

        {loading && <div className="replay-modal-loading">Loading signed replay…</div>}
        {error && <div className="replay-modal-error" data-testid="replay-modal-error">{error}</div>}

        {payload && (
          <>
            {/* Signature badge — live ed25519 verify result */}
            <div className="replay-modal-badges">
              {verify === 'pending' && (
                <span className="replay-badge pending" data-testid="replay-verify-pending">
                  ⏳ Verifying signature…
                </span>
              )}
              {verify?.ok === true && (
                <span className="replay-badge ok" data-testid="replay-verify-ok" title={verify.reason}>
                  ✓ Ed25519 signature verified
                </span>
              )}
              {verify?.ok === false && (
                <span className="replay-badge bad" data-testid="replay-verify-bad" title={verify.reason}>
                  ✗ Signature mismatch — file is not authentic
                </span>
              )}
              {verify?.ok === null && (
                <span className="replay-badge warn" data-testid="replay-verify-unavailable" title={verify.reason}>
                  ⚠ Browser can't verify — download & check offline
                </span>
              )}
              <span className="replay-badge meta" title="Key that signed this replay">
                🔑 {pub?.key_id?.slice(-6) || payload.key_id?.slice(-6) || '—'}
              </span>
              <span className="replay-badge meta">
                {trades.length} trade{trades.length === 1 ? '' : 's'}
              </span>
              <span className="replay-badge meta">
                Generated {fmtRelative(payload.generated_at)}
              </span>
            </div>

            {/* Trades table */}
            <div className="replay-modal-scroll" data-testid="replay-table-scroll">
              {trades.length === 0 ? (
                <div className="replay-modal-empty">
                  No trades on this Clan yet. Come back after your first buys.
                </div>
              ) : (
                <table className="replay-table">
                  <thead>
                    <tr>
                      <th style={{ width: 42 }}>#</th>
                      <th>Time (µs)</th>
                      <th>Side</th>
                      <th style={{ textAlign: 'right' }}>Qty</th>
                      <th style={{ textAlign: 'right' }}>Avg price</th>
                      <th style={{ textAlign: 'right' }}>Subtotal</th>
                      <th style={{ textAlign: 'right' }}>Bits</th>
                      <th>Batch</th>
                      <th>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trades.map((t, i) => {
                      const avg = t.qty > 0 ? t.subtotal / t.qty : 0;
                      return (
                        <tr key={t.trade_id} data-testid={`replay-row-${i}`}>
                          <td className="replay-mono">{i + 1}</td>
                          <td className="replay-mono">{fmtUs(t.arrived_at_us) || fmtWall(t.created_at)}</td>
                          <td>
                            <span className={`replay-side ${t.side}`}>{t.side === 'buy' ? '▲ Buy' : '▼ Sell'}</span>
                          </td>
                          <td className="replay-mono" style={{ textAlign: 'right' }}>{t.qty}</td>
                          <td className="replay-mono" style={{ textAlign: 'right' }}>{avg.toFixed(4)}</td>
                          <td className="replay-mono" style={{ textAlign: 'right' }}>{Number(t.subtotal).toFixed(2)}</td>
                          <td className="replay-mono" style={{ textAlign: 'right' }}>
                            {t.bits_before} → {t.bits_after}
                          </td>
                          <td className="replay-mono replay-dim">
                            {t.batch_size ? `${t.batch_position}/${t.batch_size}` : '—'}
                          </td>
                          <td className="replay-mono replay-dim" title={t.user_hash || ''}>
                            {t.user_hash ? t.user_hash.replace(/^sha256:/, '').slice(0, 8) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer actions */}
            <div className="replay-modal-foot">
              <div className="replay-modal-foot-hint">
                Every row is inside the signature. Change one byte, the ✓ turns into ✗.
              </div>
              <div className="replay-modal-foot-actions">
                <button
                  type="button"
                  className="replay-modal-btn secondary"
                  onClick={onCopySig}
                  data-testid="replay-copy-sig"
                >
                  {copied ? '✓ Copied' : 'Copy signature'}
                </button>
                <button
                  type="button"
                  className="replay-modal-btn primary"
                  onClick={onDownload}
                  data-testid="replay-download-json"
                >
                  ↓ Download JSON
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- formatting helpers ----
function fmtUs(us) {
  if (!us || typeof us !== 'number') return null;
  const ms = Math.floor(us / 1000);
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const micros = String(us % 1_000_000).padStart(6, '0');
  return `${hh}:${mm}:${ss}.${micros}`;
}
function fmtWall(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString();
}
function fmtRelative(iso) {
  if (!iso) return '—';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
